import React, {
    lazy,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useAutocomplete } from 'component/Layout/Autocomplete/__hooks/useAutocomplete';
import { useListNavigation } from 'component/Layout/Autocomplete/__hooks/useListNavigation';
import DefaultAutocompleter from 'component/Layout/Autocomplete/__partials/Autocompleter';

import classNames from 'classnames';
import { useSetAtom } from 'jotai';

import { QaIdentifier, useQa } from 'hooks/useQa';
import { useDebounce } from 'hooks/Wait/useDebounce';

import { refreshNoteVisibility } from 'data/Atom/Component';

import { lazyRetry } from 'utility/Lazy/Retry';
import { CommunicationContext } from 'utility/Provide/Context/PageContext';

import {
    useDestinationAutocompleterAction,
    useDestinationAutocompleterState,
} from './__context';
import { getTargetDistanceLabel } from './__helper/getTargetDistanceLabel';
import { useDestinationValue } from './__hooks/useDestinationValue';
import InitialList from './__partials/InitialList';
import {
    DestinationEntry,
    SearchProps,
    TargetDistance,
    TopRegion,
} from './types';

import styles from './Destination.module.scss';

const LazyAutocompleterList = lazy(() =>
    lazyRetry(() => import('./__partials/Autocompleter/AutocompleterList'))
);

const Search = ({
    customClassList,
    destinationValue,
    onValueChange,
    onEmptyDestinationClick,
    placeholder = '',
    label = '',
}: SearchProps) => {
    const destinationState = useDestinationAutocompleterState();
    const destinationAction = useDestinationAutocompleterAction();
    const debouncer = useDebounce();
    const { t } = useTranslation();
    const setRefreshNoteVisibility = useSetAtom(refreshNoteVisibility);
    const qaAttr = useQa(
        QaIdentifier.Search_Form_Destination_Layer_Autocompleter
    );
    const {
        toggle,
        onToggle,
        state,
        action,
        inputValue,
        setInputValue,
        autocompleteWrapperRef,
        onSearch,
        onClear,
    } = useAutocomplete(destinationState, destinationAction, {
        onClose: () => {
            setInputValue(lastValidInput.current);
        },
    });

    const {
        updateTopRegion,
        updateFlightDistance,
        updateListElement,
        resetDestination,
    } = useDestinationValue({
        onDestinationValueChange: onValueChange,
        destinationValue,
    });

    const [hasListEntries, setHasListEntries] = useState(false);
    const [isInitialList, setIsInitialList] = useState(false);
    const lastValidInput = useRef(inputValue);
    const communication = useContext(CommunicationContext);
    const leafletService =
        communication.components.travel?.leafletListUnifiedLayer;

    const onSelectListEntry = useListNavigation<DestinationEntry>({
        defaultValues: state.currentValue,
    });

    const onToggle_ = useCallback(
        (value: boolean) => {
            // reset view, if not visible
            if (!value) {
                setHasListEntries(false);
                setIsInitialList(false);
                setInputValue(lastValidInput.current);
            }
            onToggle(value);
        },
        [onToggle, setInputValue]
    );

    const onListElementClick = useCallback(
        async (value: DestinationEntry) => {
            const destinationLabel =
                value.destinationTree !== ''
                    ? `, ${value.destinationTree}`
                    : '';
            const inputValue = `${value.label}${destinationLabel}`;
            const selectedValues = onSelectListEntry(value);
            action.setValue(selectedValues);
            setInputValue(inputValue);
            lastValidInput.current = inputValue;
            onToggle_(false);
            updateListElement(value, inputValue);
        },
        [action, onSelectListEntry, onToggle_, setInputValue, updateListElement]
    );

    const onSearch_ = useCallback(
        async (value: string) => {
            setRefreshNoteVisibility(false);
            setInputValue(value);
            setHasListEntries(false);
            setIsInitialList(value.length === 0);
            // wait for search, if input is ready
            // (500ms after last input, execute function)
            debouncer(async () => {
                const results = await onSearch(value);
                // set results
                if (results) {
                    action.setResults(results);
                    setHasListEntries(results.length > 0);
                    // if searchinput is empty, set results empty
                } else if (results === null) {
                    action.setResults([]);
                }
                setIsInitialList(results?.length === 0);
            }, 500);
        },
        [action, debouncer, onSearch, setInputValue, setRefreshNoteVisibility]
    );

    const onTopRegionClick_ = useCallback(
        (entry: TopRegion) => {
            setRefreshNoteVisibility(false);
            const inputValue = `${entry.name}, ${entry.countryName}`;
            onToggle_(false);
            setInputValue(inputValue);
            lastValidInput.current = inputValue;
            updateTopRegion(entry, inputValue);
        },
        [onToggle_, setInputValue, updateTopRegion, setRefreshNoteVisibility]
    );

    const onTargetDistanceClick_ = useCallback(
        (entry: TargetDistance) => {
            setRefreshNoteVisibility(false);
            const inputValue = getTargetDistanceLabel(entry.value, t);
            onToggle_(false);
            setInputValue(inputValue);
            lastValidInput.current = inputValue;
            updateFlightDistance(entry, inputValue);
        },
        [
            onToggle_,
            setInputValue,
            t,
            updateFlightDistance,
            setRefreshNoteVisibility,
        ]
    );
    const onEmptyDestinationClick_ = useCallback(
        (entry: string) => {
            onToggle_(false);
            setInputValue(entry);
            lastValidInput.current = entry;
            onEmptyDestinationClick?.();
            resetDestination();
        },
        [onEmptyDestinationClick, onToggle_, resetDestination, setInputValue]
    );

    // set searchinput by Destination-Label
    useEffect(() => {
        if (destinationValue.label) {
            setInputValue(destinationValue.label);
            lastValidInput.current = destinationValue.label;
        }
    }, [destinationValue.label, setInputValue]);

    // sync leaflet service with current destination value
    useEffect(() => {
        if (leafletService && inputValue) {
            leafletService.handleDynamicDataUpdate(inputValue);
        }
    });

    return (
        <DefaultAutocompleter
            autocompleterWrapperRef={
                autocompleteWrapperRef as React.RefObject<HTMLDivElement>
            }
            label={label}
            placeholder={placeholder}
            onSearchInputClick={() => {
                onToggle_(true);
                setIsInitialList(true);
                setHasListEntries(false);
            }}
            toggle={toggle}
            onSearch={onSearch_}
            searchValue={inputValue}
            onClear={onClear}
            onFocus={() => setInputValue('')}
            errorMessage={undefined}
            customClassList={{
                InputWrapper: customClassList?.InputWrapper,
                Input: customClassList?.Input,
                Label: customClassList?.Label,
                autocompleterWrapper: customClassList?.autocompleterWrapper,
            }}
            qaId={QaIdentifier.Search_Form_Destination_Input}
        >
            {hasListEntries ? (
                <div
                    className={classNames(
                        styles.ScrollContainer,
                        styles.ListWrapper,
                        customClassList?.Layer
                    )}
                    {...qaAttr}
                >
                    {state.results?.map((section) => (
                        <LazyAutocompleterList
                            key={section.label}
                            section={section}
                            onClick={onListElementClick}
                        />
                    ))}
                    <div className={styles.Hint}>
                        {t(
                            'TRANS-destination-not-found-try-to-be-more-specific'
                        )}
                    </div>
                </div>
            ) : (
                isInitialList && (
                    <InitialList
                        onTopRegionClick={onTopRegionClick_}
                        onEmptyDestinationClick={onEmptyDestinationClick_}
                        onTargetDistanceClick={onTargetDistanceClick_}
                        customClassList={customClassList}
                    />
                )
            )}
        </DefaultAutocompleter>
    );
};

export default Search;
